import React from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';

const LIGHT_PURPLE = '#A76FFF';

const WebTermsOfServiceScreen: React.FC = () => {
    const navigate = useNavigate();

    return (
        <>
            <SEOHead
                title="Terms of Service - Pop The Balloon Dating App"
                description="Read the terms of service for Pop The Balloon dating app. Understand our user guidelines and legal terms for using our platform."
                canonical="https://www.poptheballoonapp.com/terms-of-service"
                ogTitle="Terms of Service - Pop The Balloon"
                ogDescription="Review the terms and conditions for using Pop The Balloon dating app safely and responsibly."
                ogUrl="https://www.poptheballoonapp.com/terms-of-service"
            />
            <SiteHeader />
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconButton onClick={() => navigate(-1)} size="large" sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                            Terms of Service
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 4, fontStyle: 'italic' }}>
                        Last Updated: October 18, 2026
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Welcome to Pop The Balloon ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our dating application and related services (collectively, the "Service").
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            1. Acceptance of Terms
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            By creating an account or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            2. Eligibility
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            You must be at least 18 years old to use the Service. By using the Service, you represent and warrant that you meet this age requirement.
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            3. Account Registration
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            You agree to:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Provide accurate, current, and complete information during registration
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Maintain and promptly update your account information
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Keep your password secure and confidential
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Notify us immediately of any unauthorized use of your account
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • Be responsible for all activities that occur under your account
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            4. Subscription Services
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            4.1 Subscription Plans
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            The core Service, including daily picks, matching and chat, is free. We also offer two optional subscription plans, Golden and Platinum, on monthly or annual terms. They add features such as more daily picks, rewinds, boosts, Golden Balloons and AI Insights. The features and limits of each plan are shown in the app before you buy and may change over time.
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            4.2 Subscription Pricing
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Prices are shown in the app and in Google Play or the App Store before you buy. They are in your local currency and may vary by region. Annual plans are discounted compared with paying monthly.
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            4.3 Free Trial
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Some new members may be offered a free trial of the Golden plan. The length of the trial is shown in the app before you start it, and Google Play or Apple decides whether your account is eligible (usually one trial per account).
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • You will not be charged during the trial period
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • The trial turns into a paid subscription when it ends unless you cancel at least 24 hours before the end of the trial
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • Free trials are non-transferable
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500, color: 'error.main' }}>
                            4.4 Auto-Renewal and Billing
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: 'error.main' }}>
                            IMPORTANT AUTO-RENEWAL DISCLOSURE:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>Automatic Renewal</strong>: Your subscription renews automatically at the end of each billing period (monthly or annual) unless you cancel at least 24 hours before the end of the current period
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>Billing</strong>: Payment is charged to your Google Play or Apple account when you confirm your purchase, and at the start of each renewal period at the then-current price for your plan and region
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>Price Changes</strong>: If the price of your subscription goes up, you will be told before the new price applies, as Google Play and Apple require, and you can cancel before then
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • <strong>Failed Payments</strong>: If a renewal payment fails, Google Play or Apple may retry it, and your plan features may pause until the payment goes through
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            4.5 Cancellation Policy
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            You may cancel your subscription at any time:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>How to Cancel</strong>: On Android, open Google Play and go to Payments &amp; subscriptions, then Subscriptions. On iPhone, open Settings, tap your name, then Subscriptions. Deleting the app or your account does not cancel a subscription
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>Effect of Cancellation</strong>: When you cancel, your subscription will remain active until the end of your current billing period. You will continue to have access to premium features until that date
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>No Partial Refunds</strong>: Cancellation does not entitle you to a refund for the current billing period
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • <strong>Resubscribing</strong>: You can subscribe again at any time in the app
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            4.6 Refund Policy
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                            Purchases are made through Google Play or the App Store, so refunds follow their refund policies. Apart from those policies, payments are non-refundable except as required by law.
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>iPhone</strong>: Apple handles all refunds for App Store purchases. You can request one at reportaproblem.apple.com
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>Android</strong>: You can request a refund through Google Play, or contact us and we will review your request
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • <strong>Unauthorized Charges</strong>: If you believe you were charged in error or without authorization, contact Google Play or Apple, and us, straight away
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • <strong>Legal Requirements</strong>: Refunds will be provided as required by applicable law in your jurisdiction
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            5. In-App Purchases
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            We also sell one-off packs in the app: Golden Balloons, Boosts, extra daily picks and AI Insights. Packs are used up as you use them, and extra picks are added to that day's picks. All purchases are final and non-refundable except as required by law or under the store's refund policy (see section 4.6).
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            6. User Conduct
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            You agree not to:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Use the Service for any illegal purpose
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Harass, abuse, or harm other users
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Impersonate any person or entity
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Upload false, misleading, or inappropriate content
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Attempt to gain unauthorized access to the Service
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Use automated systems (bots) to access the Service
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • Violate any applicable laws or regulations
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            7. Account Termination
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            7.1 Termination by You
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            You may delete your account at any time through the app settings. Upon account deletion:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Deleting your account does not cancel a subscription bought through Google Play or the App Store, so cancel it there first (see section 4.5)
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • You will lose access to all premium features and any unused packs
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Your profile and data will be deleted according to our Privacy Policy
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • No refunds will be provided for the current billing period
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                            7.2 Termination by Us
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            We may suspend or terminate your account if:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • You violate these Terms
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • You engage in fraudulent activity
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Your account is inactive for an extended period
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • Required by law
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            8. Payment Processing
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Purchases in the app are processed by Google Play (on Android) or Apple (on iPhone) under their terms, using the payment method saved to your store account. We use RevenueCat to keep track of your subscriptions and purchases. We never receive or store your full card details.
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            9. Disclaimers
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • That the Service will be uninterrupted or error-free
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • That defects will be corrected
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • That the Service is free of viruses or harmful components
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • The accuracy or reliability of any content
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • That you will find a romantic match
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            10. Limitation of Liability
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • WE ARE NOT LIABLE FOR USER CONDUCT OR CONTENT
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            11. Changes to Terms
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            We may modify these Terms at any time. We will notify you of material changes:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Through the app
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            • Via email
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            • By posting the updated Terms with a new "Last Updated" date
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Your continued use of the Service after changes constitutes acceptance of the new Terms.
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                            12. Contact Information
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            For questions about these Terms or the Service, please contact us.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate(-1)}
                            sx={{
                                bgcolor: LIGHT_PURPLE,
                                borderRadius: 5,
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: '#8A5CF5',
                                },
                            }}
                        >
                            I Agree
                        </Button>
                    </Box>
                </Paper>
            </Container>
            <AppFooter />
        </>
    );
};

export default WebTermsOfServiceScreen; 